import React from 'react';
import {
    Box, Typography, Card, CardContent, Container, Button,
    useTheme, useMediaQuery, CircularProgress, Chip
} from "@mui/material";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { Calendar, Clock, Video, CalendarCheck, CalendarPlus, RotateCw, X } from 'lucide-react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import CustomAppBar from "../../components/CustomAppBar";
import PortalFabMenu from "../../components/PortalFabMenu";
import PortalHeader from "../../components/PortalHeader";
import AddToCalendarButton from "../../components/AddToCalendarButton";
import CancelAppointmentDialog from "../../components/CancelAppointmentDialog";
import { typographySx } from "../../lib/uiformatting";
import { callBackendApiGet, callBackendApiPost } from "../../lib/api";
import { updatePortalData } from '../../redux/portalSlice';
import { useAuth0 } from "@auth0/auth0-react";
import { setEmail, setPhoneNo, setSelectedAppointmentPatient, setAppointmentToReschedule } from '../../redux/userSlice';
import logDrtEvent from '../../lib/logging';

export default function AppointmentList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigationType = useNavigationType();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    const { getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0();

    const [loading, setLoading] = React.useState(false);
    const [showAllPending, setShowAllPending] = React.useState(false);
    const [showCompletedAppointments, setShowCompletedAppointments] = React.useState(false);
    const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false);
    const [appointmentToCancel, setAppointmentToCancel] = React.useState(null);
    
    const INITIAL_APPOINTMENTS_TO_SHOW = 3;

    const userFirstName = useSelector(s => s.user.firstName);
    const userEmail = useSelector(s => s.user.email);
    const userPhone = useSelector(s => s.user.phoneNo);
    const patients = useSelector(s => s.portal.patients, shallowEqual);
    const appointmentsByPatient = useSelector(s => s.portal.appointments, shallowEqual);

    const cameViaBottomNav = location.state?.viaBottomNav === true;
    const arrivedViaInAppNav = cameViaBottomNav && (navigationType === 'PUSH' || navigationType === 'REPLACE');
    const initialSkipRef = React.useRef(arrivedViaInAppNav);
    const viaBooking = location.state?.viaBooking === true;

    React.useEffect(() => {
        if (cameViaBottomNav || viaBooking) {
            navigate(location.pathname + location.search, { replace: true, state: {} });
        }
    }, [cameViaBottomNav, viaBooking, navigate, location.pathname, location.search]);

    const didFetchRef = React.useRef(false);

    const fetchPortalData = React.useCallback(async ({ bypassGuard = false } = {}) => {
        if (!user?.phone_number) return;

        if (didFetchRef.current && !bypassGuard) return;
        if (!bypassGuard) didFetchRef.current = true;

        try {
            setLoading(true);
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
            });
            const res = await callBackendApiGet(
                `/api/v1/portal/patient?phone=${encodeURIComponent(user.phone_number)}`,
                {},
                token
            );
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();

            if (Array.isArray(data) && data.length > 0) {
                dispatch(updatePortalData({ patientData: data }));
                const patient = data[0];
                if (patient) {
                    if (patient?.contact?.email) dispatch(setEmail(patient?.contact?.email));
                    if (patient?.contact?.phone) dispatch(setPhoneNo(patient?.contact?.phone));
                }
            }
        } catch (err) {
            logDrtEvent(`Error while fetching portal information for patients with phone: ${err}`);
        } finally {
            setLoading(false);
        }
    }, [user, dispatch, getAccessTokenSilently]);

    React.useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) return;

        if (viaBooking) {
            // After booking, we need fresh appointments immediately. This bypasses the "already fetched" guard so the new booking appears
            fetchPortalData({ bypassGuard: true });
            return;
        }

        if (initialSkipRef.current) {
            initialSkipRef.current = false;
            didFetchRef.current = true;
            setLoading(false);
            return;
        }
        fetchPortalData();
    }, [isLoading, isAuthenticated, viaBooking, fetchPortalData]);

    const { pendingAppointments, completedAppointments } = React.useMemo(() => {
        const pending = [];
        const completed = [];
        
        if (!appointmentsByPatient) return { pendingAppointments: pending, completedAppointments: completed };

        Object.entries(appointmentsByPatient).forEach(([patientID, appts]) => {
            const patientName = patients?.[patientID]?.full_name || 'Unknown patient';
            if (Array.isArray(appts)) {
                appts.forEach(a => {
                    const appointment = {
                        id: a.id,
                        patientID,
                        patientName,
                        appointment_time: a.appointment_time,
                        status: a.status,
                        video_link: a.video_link,
                    };
                    
                    // TODO: Add more statuses as they are added to the backend
                    // Currently pushing all statuses to the completed array.
                    // Separate array for each status in the future if needed.
                    const isCompleted = (a.status || '').toLowerCase() === 'completed';
                    const isCancelled = (a.status || '').toLowerCase() === 'cancelled';
                    const isNoShow = (a.status || '').toLowerCase() === 'no_show';
                    const isRescheduled = (a.status || '').toLowerCase() === 'rescheduled';
                    if (isCompleted || isCancelled || isNoShow || isRescheduled) {
                        completed.push(appointment);
                    } else {
                        pending.push(appointment);
                    }
                });
            }
        });

        // Sort both arrays in reverse chronological order
        pending.sort((a, b) => new Date(b.appointment_time) - new Date(a.appointment_time));
        completed.sort((a, b) => new Date(b.appointment_time) - new Date(a.appointment_time));
        
        return { pendingAppointments: pending, completedAppointments: completed };
    }, [appointmentsByPatient, patients]);

    const containerStyle = {
        maxWidth: isDesktop ? '1000px' : 'sm',
        pt: isDesktop ? 4 : 2,
        pb: isDesktop ? 4 : 2,
        px: isDesktop ? 4 : 4,
    };

    const openVideo = (url) => {
        if (!url) return;
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    };

    const renderStatusChip = (status) => {
        if (!status || status.toLowerCase() === 'pending') {
            return null;
        }

        const statusLower = status.toLowerCase();
        const statusLabel = status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
        
        const getStatusColors = () => {
            if (statusLower === 'completed') {
                return { bg: '#ccfbf1', text: '#0d9488' };
            } else if (statusLower === 'cancelled') {
                return { bg: '#fee2e2', text: '#dc2626' };
            } else {
                return { bg: '#fef3c7', text: '#d97706' };
            }
        };

        const colors = getStatusColors();

        return (
            <Chip
                label={statusLabel}
                size="small"
                sx={{
                    mt: 0.5,
                    height: '20px',
                    fontSize: '0.65rem',
                    fontWeight: 500,
                    backgroundColor: colors.bg,
                    color: colors.text,
                }}
            />
        );
    };

    const cancelAppointment = React.useCallback(async (appointmentId, reason) => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
            });
            const res = await callBackendApiPost(
                `/api/v1/portal/cancel-appointment/${appointmentId}`,
                { cancellation_reason: reason || null },
                token
            );
            if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
            const data = await res.json();
            // Refresh patient data to get the latest appointments from the backend
            await fetchPortalData({ bypassGuard: true });
        } catch (err) {
            logDrtEvent(`Error while cancelling appointment: ${err}`);
        }
    }, [getAccessTokenSilently, fetchPortalData]);

    return (
        <Box sx={{
            pb: 7,
            bgcolor: 'white',
            minHeight: '100vh',
            background: 'linear-gradient(to bottom right, rgba(239, 246, 255, 0.4), rgba(240, 253, 250, 0.3), rgba(255, 247, 237, 0.2))',
        }}>
            <CustomAppBar noBack={true} returning={false} portal={true} />

            <Container sx={containerStyle}>
                <PortalHeader 
                    userFirstName={userFirstName}
                    userEmail={userEmail}
                    userPhone={userPhone}
                    user={user}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            backgroundColor: '#1a3a5c',
                            borderRadius: 2,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <CalendarMonthIcon sx={{ color: 'white', fontSize: 25 }} />
                        </Box>
                        <Box>
                            <Typography variant="h7" component="div" sx={{ ...typographySx, mb: 0, fontSize: '1.4rem' }}>
                                My Appointments
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                                View and join your scheduled dermatology appointments
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* Book New Appointment Button */}
                <Button
                    variant="contained"
                    startIcon={<CalendarPlus size={20} color="white" />}
                    onClick={() => navigate("/appointment-booking-patients", { state: { portal: true } })}
                    sx={{
                        width: '100%',
                        background: 'linear-gradient(to right, #003B5C, #004C73)',
                        color: 'white',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        height: '48px',
                        mb: 2,
                        mt: 3,
                        boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
                        '&:hover': {
                            background: 'linear-gradient(to right, #00294A, #003B5C)',
                        },
                    }}
                >
                    Book New Appointment
                </Button>

                <Typography variant="body1" component="div" sx={{ ...typographySx, mb: 2, mt: 0, fontSize: '1.25rem', fontWeight: 500 }}>
                    Upcoming Appointments
                </Typography>

                {(isLoading || loading) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                ) : (
                    <>
                        {(pendingAppointments.length > 0 || completedAppointments.length > 0) ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                {/* Pending Appointments */}
                                {(showAllPending ? pendingAppointments : pendingAppointments.slice(0, INITIAL_APPOINTMENTS_TO_SHOW)).map((appt) => {
                                    const isCompleted = false;

                                    return (
                                        <Card
                                            key={appt.id}
                                            elevation={0}
                                            sx={{
                                                mx: isDesktop ? 'auto' : 2,
                                                width: '100%',
                                                background: isCompleted
                                                    ? 'linear-gradient(to bottom right, rgba(249, 250, 251, 0.8), rgba(248, 250, 252, 0.6))'
                                                    : 'linear-gradient(to bottom right, rgba(236, 254, 255, 0.8), rgba(239, 246, 255, 0.6))',
                                                border: '1px solid',
                                                borderColor: isCompleted ? '#e5e7eb' : '#cffafe',
                                                borderRadius: '12px',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                                overflow: 'hidden',
                                                transition: 'box-shadow 0.2s, border-color 0.2s',
                                                '&:hover': {
                                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                                                    borderColor: isCompleted ? '#e5e7eb' : '#a5f3fc',
                                                },
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    p: isDesktop ? 3 : 2.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5,
                                                }}
                                            >
                                                {/* Title and Subtitle */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#1a1a1a',
                                                                fontSize: '1rem',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {appt.patientName}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#5a5a6e',
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            Video Visit
                                                        </Typography>
                                                        {renderStatusChip(appt.status)}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: !isCompleted ? 'rgba(8, 145, 178, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                                                            borderRadius: '50%',
                                                            p: 0.75,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            ml: 1,
                                                        }}
                                                    >
                                                        <CalendarCheck 
                                                            size={16} 
                                                            color={!isCompleted ? '#0891b2' : '#6b7280'} 
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Date */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Calendar size={18} color="#8b8b8b" />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#1a1a1a',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {formatDate(appt.appointment_time)}
                                                    </Typography>
                                                </Box>

                                                {/* Time and Duration */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Clock size={18} color="#8b8b8b" />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#1a1a1a',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {formatTime(appt.appointment_time)} • 15 min
                                                    </Typography>
                                                </Box>

                                                {/* Join Button */}
                                                {!isCompleted && (
                                                    <Button
                                                        variant="contained"
                                                        disabled={!appt.video_link}
                                                        onClick={() => openVideo(appt.video_link)}
                                                        startIcon={<Video size={20} color="white" />}
                                                        sx={{
                                                            mt: 0.5,
                                                            width: '100%',
                                                            backgroundColor: '#1a3a5c',
                                                            color: 'white',
                                                            borderRadius: '12px',
                                                            fontFamily: 'Inter, sans-serif',
                                                            fontWeight: 600,
                                                            py: 1,
                                                            textTransform: 'none',
                                                            fontSize: '0.75rem',
                                                            '&:hover': {
                                                                backgroundColor: '#2a4a6c',
                                                            },
                                                            '&:disabled': {
                                                                backgroundColor: '#cccccc',
                                                                color: '#666666',
                                                            },
                                                        }}
                                                    >
                                                        Join Video Call
                                                    </Button>
                                                )}

                                                {/* Action Buttons */}
                                                {!isCompleted && (
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                                                        <AddToCalendarButton appointment={appt} />
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<RotateCw size={14} color="#2e8da6" />}
                                                            onClick={() => {
                                                                dispatch(setSelectedAppointmentPatient(patients[appt.patientID]));
                                                                dispatch(setAppointmentToReschedule(appt));
                                                                navigate('/appointment', { state: { portal: true } });
                                                            }}
                                                            sx={{
                                                                color: '#2e8da6',
                                                                textTransform: 'none',
                                                                fontWeight: 500,
                                                                fontSize: '0.65rem',
                                                                py: 0.5,
                                                                px: 1,
                                                                borderRadius: '10px',
                                                                border: '1px solid #2e8da6 !important',
                                                                backgroundColor: 'transparent',
                                                                '& .MuiButton-startIcon': {
                                                                    marginRight: '4px',
                                                                    marginLeft: 0,
                                                                },
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(46, 141, 166, 0.1)',
                                                                    border: '1px solid #2e8da6 !important',
                                                                },
                                                            }}
                                                        >
                                                            Reschedule
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            startIcon={<X size={14} color="#ef4444" />}
                                                            onClick={() => {
                                                                setAppointmentToCancel(appt);
                                                                setCancelDialogOpen(true);
                                                            }}
                                                            sx={{
                                                                color: '#ef4444',
                                                                textTransform: 'none',
                                                                fontWeight: 500,
                                                                fontSize: '0.65rem',
                                                                py: 0.5,
                                                                px: 1,
                                                                borderRadius: '10px',
                                                                border: '1px solid #ef4444 !important',
                                                                backgroundColor: 'transparent',
                                                                '& .MuiButton-startIcon': {
                                                                    marginRight: '4px',
                                                                    marginLeft: 0,
                                                                },
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                                                    border: '1px solid #ef4444 !important',
                                                                },
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    );
                                })}

                                {/* Show More/Less Button for Pending */}
                                {pendingAppointments.length > INITIAL_APPOINTMENTS_TO_SHOW && (
                                    <Button
                                        onClick={() => setShowAllPending(!showAllPending)}
                                        sx={{
                                            mt: 1,
                                            mb: 2,
                                            width: '100%',
                                            backgroundColor: 'white',
                                            color: '#1a1a1a',
                                            border: '1px solid',
                                            borderColor: '#cffafe',
                                            borderRadius: '12px',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 0.5,
                                            fontSize: '0.85rem',
                                            '&:hover': {
                                                backgroundColor: '#f0f9ff',
                                                borderColor: '#a5f3fc',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        {showAllPending ? 'Show Less' : `Show More (${pendingAppointments.length - INITIAL_APPOINTMENTS_TO_SHOW} more)`}
                                    </Button>
                                )}

                                {/* Completed Appointments Button */}
                                {completedAppointments.length > 0 && !showCompletedAppointments && (
                                    <Button
                                        onClick={() => setShowCompletedAppointments(true)}
                                        sx={{
                                            mt: pendingAppointments.length > 0 ? 2 : 0,
                                            mb: 2,
                                            width: '100%',
                                            backgroundColor: 'white',
                                            color: '#1a1a1a',
                                            border: '1px solid',
                                            borderColor: '#e5e7eb',
                                            borderRadius: '12px',
                                            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            py: 0.5,
                                            fontSize: '0.85rem',
                                            '&:hover': {
                                                backgroundColor: '#f9fafb',
                                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
                                            },
                                        }}
                                    >
                                        Show All Past Appointments ({completedAppointments.length} more)
                                    </Button>
                                )}

                                {/* Completed Appointments */}
                                {showCompletedAppointments && completedAppointments.length > 0 && (
                                    <Typography 
                                        variant="body1" 
                                        component="div" 
                                        sx={{ 
                                            ...typographySx, 
                                            mb: 2, 
                                            mt: pendingAppointments.length > 0 ? 4 : 0,
                                            width: '100%',
                                            textAlign: 'left',
                                            fontSize: '1.25rem',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Past Appointments
                                    </Typography>
                                )}
                                {showCompletedAppointments && completedAppointments.map((appt) => {
                                    const isCompleted = true;

                                    return (
                                        <Card
                                            key={appt.id}
                                            elevation={0}
                                            sx={{
                                                mx: isDesktop ? 'auto' : 2,
                                                width: '100%',
                                                background: isCompleted
                                                    ? 'linear-gradient(to bottom right, rgba(249, 250, 251, 0.8), rgba(248, 250, 252, 0.6))'
                                                    : 'linear-gradient(to bottom right, rgba(236, 254, 255, 0.8), rgba(239, 246, 255, 0.6))',
                                                border: '1px solid',
                                                borderColor: isCompleted ? '#e5e7eb' : '#cffafe',
                                                borderRadius: '12px',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                                overflow: 'hidden',
                                                transition: 'box-shadow 0.2s, border-color 0.2s',
                                                '&:hover': {
                                                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -1px rgb(0 0 0 / 0.06)',
                                                    borderColor: isCompleted ? '#e5e7eb' : '#a5f3fc',
                                                },
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    p: isDesktop ? 3 : 2.5,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1.5,
                                                }}
                                            >
                                                {/* Title and Subtitle */}
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                    <Box sx={{ flex: 1 }}>
                                                        <Typography
                                                            variant="h6"
                                                            sx={{
                                                                fontWeight: 600,
                                                                color: '#1a1a1a',
                                                                fontSize: '1rem',
                                                                mb: 0.5,
                                                            }}
                                                        >
                                                            {appt.patientName}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                color: '#5a5a6e',
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            Video Visit
                                                        </Typography>
                                                        {renderStatusChip(appt.status)}
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: !isCompleted ? 'rgba(8, 145, 178, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                                                            borderRadius: '50%',
                                                            p: 0.75,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            ml: 1,
                                                        }}
                                                    >
                                                        <CalendarCheck 
                                                            size={16} 
                                                            color={!isCompleted ? '#0891b2' : '#6b7280'} 
                                                        />
                                                    </Box>
                                                </Box>

                                                {/* Date */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Calendar size={18} color="#8b8b8b" />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#1a1a1a',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {formatDate(appt.appointment_time)}
                                                    </Typography>
                                                </Box>

                                                {/* Time and Duration */}
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                                    <Clock size={18} color="#8b8b8b" />
                                                    <Typography
                                                        variant="body2"
                                                        sx={{
                                                            color: '#1a1a1a',
                                                            fontSize: '0.75rem',
                                                        }}
                                                    >
                                                        {formatTime(appt.appointment_time)} • 15 min
                                                    </Typography>
                                                </Box>

                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Box>
                        ) : (
                            <Container maxWidth="sm" sx={{ mt: 4 }}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                                    {/* Icon */}
                                    <Box
                                        sx={{
                                            width: '64px',
                                            height: '64px',
                                            borderRadius: '12px',
                                            background: 'linear-gradient(to bottom right, rgba(239, 246, 255, 0.8), rgba(240, 253, 250, 0.6))',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mb: 3,
                                        }}
                                    >
                                        <Calendar size={32} color="#1e40af" />
                                    </Box>

                                    {/* Heading */}
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 700,
                                            color: '#1a1a1a',
                                            fontSize: '1.2rem',
                                            mb: 1.5,
                                        }}
                                    >
                                        No Appointments Yet
                                    </Typography>

                                    {/* Descriptive Paragraph */}
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#6b7280',
                                            fontSize: '0.84rem',
                                            mb: 3,
                                            maxWidth: '400px',
                                        }}
                                    >
                                        You haven't scheduled any appointments with Piction Health.
                                    </Typography>
                                </Box>
                            </Container>
                        )}
                    </>
                )}
            </Container >

            {/* Cancel Appointment Confirmation Dialog */}
            <CancelAppointmentDialog
                open={cancelDialogOpen}
                onClose={() => {
                    setCancelDialogOpen(false);
                    setAppointmentToCancel(null);
                }}
                onConfirm={(reason) => {
                    if (appointmentToCancel) {
                        logDrtEvent(`User cancelled appointment: ${appointmentToCancel.patientName}, reason: ${reason || 'not provided'}`);
                        cancelAppointment(appointmentToCancel.id, reason);
                    } else {
                        logDrtEvent(`User cancelled appointment: No appointment selected`);
                        return;
                    }
                    setCancelDialogOpen(false);
                    setAppointmentToCancel(null);
                }}
                appointment={appointmentToCancel}
            />

            {pendingAppointments.length > 0 && <PortalFabMenu navigate={navigate} />}
        </Box>
    );
}
